"use client";

import React, { createContext, useContext } from "react";
import { CiteprocSelect, type CiteprocSelectOption } from "@/components/workflow/WorkflowDetailView/CiteprocSelect";
import { Skeleton } from "@/components/ui/skeleton";
import type { SupportedStyleKey, UseCiteprocResult } from "@/hooks/useCiteproc";

export type CitationStyleContextValue = {
  citeprocStyle: SupportedStyleKey;
  /** list 引用格式已就绪（避免先闪默认 GB/T 再切到接口值） */
  isCitationStyleReady: boolean;
  citeprocStyleLabel: string;
  citationFormatOptions?: CiteprocSelectOption[];
  onCiteprocStyleChange: (style: SupportedStyleKey) => void;
  /** 每次切换引用类型时递增，用于已展开行在空闲时重算 */
  citationSwitchEpoch: number;
  formatBibliography: UseCiteprocResult["formatBibliography"];
  hasCitationFeature: boolean;
};

const CitationStyleContext = createContext<CitationStyleContextValue | null>(null);

export function CitationStyleProvider({
  value,
  children,
}: {
  value: CitationStyleContextValue;
  children: React.ReactNode;
}) {
  return (
    <CitationStyleContext.Provider value={value}>
      {children}
    </CitationStyleContext.Provider>
  );
}

export function useCitationStyleContext() {
  const ctx = useContext(CitationStyleContext);
  if (!ctx) {
    throw new Error("useCitationStyleContext must be used within CitationStyleProvider");
  }
  return ctx;
}

/** 表头引用格式下拉：独立订阅 context，避免切换时重建 table columns */
export function CitationStyleHeaderSelect() {
  const {
    citeprocStyle,
    isCitationStyleReady,
    onCiteprocStyleChange,
    citationFormatOptions,
    hasCitationFeature,
  } = useCitationStyleContext();

  if (!hasCitationFeature || !citationFormatOptions?.length) {
    return null;
  }

  if (!isCitationStyleReady) {
    return <Skeleton className="h-6 w-[142px] shrink-0 rounded-[2px]" />;
  }

  return (
    <CiteprocSelect
      value={citeprocStyle}
      onChange={onCiteprocStyleChange}
      options={citationFormatOptions}
      triggerClassName="h-6 rounded-[2px] border border-border bg-white px-2 text-sm font-normal shadow-none w-[142px]"
    />
  );
}
